import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
  Flex,
  Badge,
  IconButton,
  Stack,
  Spinner,
  Icon,
} from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { ChevronDownIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import { ErrorDetailProto, FilterProto, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { FilterOperator, ColumnType } from "build-ts/enums";
import ColumnValues from "components/common/ColumnValues";
import { getFilterDisplayString } from "utils/nameUtils";
import { getDateFilterCategory } from "utils/dateUtils";
import DateFilterPicker from "modules/common/DateFilterPicker";
import { DateFilterCategory } from "utils/enum";
import { DateFilter } from "build-ts/relative_date";
import { KGObservationProto } from "build-ts/analysis_plan";
import { getKGObservationData } from "services/DataService";
import { TbFilterCheck } from "react-icons/tb";

// Define an interface for the combined dropdown items
interface DropdownItem {
  column: ObservationColumnProto;
  type: "attribute" | "temporal";
}

interface FiltersProps {
  filters: FilterProto[];
  setFilters: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  nonBenchmarkEdges: ObservationColumnProto[];
  selectedMetrics: MetricProto[];
}

const Filters: React.FC<FiltersProps> = ({ filters, setFilters, nonBenchmarkEdges, selectedMetrics }) => {
  // State for selected item and values
  const [selectedItem, setSelectedItem] = useState<DropdownItem | null>(null);
  const [attributeValues, setAttributeValues] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for date filter when temporal column is selected
  const [dateFilter, setDateFilter] = useState<DateFilter | undefined>(undefined);
  const [dateFilterCategory, setDateFilterCategory] = useState<DateFilterCategory>(DateFilterCategory.FIXED);
  const [errorMsg, setErrorMsg] = useState<string>("");
  // State for editing existing filters
  const [editingFilter, setEditingFilter] = useState<FilterProto | null>(null);
  // State to track values to be selected when editing
  const [editingValues, setEditingValues] = useState<string[]>([]);

  // Create combined dropdown items (attribute edges + temporal columns)
  const dropdownItems: DropdownItem[] = [];

  // Add attribute columns
  nonBenchmarkEdges
    ?.filter(column => column.kg_column_header?.column_type === ColumnType.ATTRIBUTE)
    .forEach(column => {
      dropdownItems.push({
        column: column,
        type: "attribute",
      });
    });

  // Add temporal columns
  nonBenchmarkEdges
    ?.filter(column => column.kg_column_header?.column_type === ColumnType.TEMPORAL)
    .forEach(column => {
      dropdownItems.push({
        column: column,
        type: "temporal",
      });
    });

  // Reset date filter and category when selected item changes
  useEffect(() => {
    if (selectedItem && selectedItem.type === "temporal") {
      setDateFilter(undefined);
      setDateFilterCategory(DateFilterCategory.FIXED);
    }
  }, [selectedItem]);

  // Fetch attribute values when an attribute item is selected
  useEffect(() => {
    if (selectedItem && selectedItem.type === "attribute" && selectedItem.column?.kg_column_header?.id) {
      const fetchValues = async () => {
        setIsLoading(true);
        try {
          const newKGObservation = KGObservationProto.create({
            metrics: selectedMetrics,
            columns: [selectedItem?.column],
            filters: filters,
            calculate_non_measure_only: true,
          });
          const values = await getKGObservationData([newKGObservation]);
          if (values?.[0]?.error_details) {
            const errorMsg = values?.[0]?.error_details?.map((err: ErrorDetailProto) => err.error_msg).join(", ");
            setErrorMsg(errorMsg);
          }
          const valuesList = JSON.parse(
            values?.[0]?.kg_observation_data?.non_measure_values?.[0]?.data_value_json_array || "[]"
          );
          setAttributeValues((valuesList || []).map(String));
        } catch (error) {
          console.error("Error fetching attribute values:", error);
          setAttributeValues([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchValues();
    }
  }, [selectedItem, selectedMetrics, filters]);

  // Effect to handle setting values when editing (both attribute and temporal)
  useEffect(() => {
    if (editingFilter && selectedItem) {
      if (selectedItem.type === "attribute" && editingValues.length > 0 && attributeValues.length > 0) {
        // For attribute filters, set the selected values after attribute values are fetched
        setSelectedValues(editingValues);
        setEditingValues([]); // Clear the editing values after setting
      } else if (selectedItem.type === "temporal" && editingFilter.date_filter) {
        // For temporal filters, set the date filter and category
        setDateFilter(editingFilter.date_filter);
        const category = getDateFilterCategory(editingFilter.date_filter);
        setDateFilterCategory(category);
      }
    }
  }, [editingFilter, selectedItem, attributeValues, editingValues]);

  // Function to add a filter or update an existing one
  const handleAddFilter = () => {
    if (!selectedItem) return;

    let newFilter: FilterProto | null = null;

    if (selectedItem.type === "attribute") {
      // For attribute type, use multi-select values
      if (selectedValues.length === 0) return;

      newFilter = FilterProto.create({
        filter_id: uuidv4(),
        kg_column_header: selectedItem?.column?.kg_column_header,
        operator: FilterOperator.EQ,
        values: selectedValues,
      });
    } else if (selectedItem.type === "temporal") {
      // For temporal type, use the DateFilter
      if (!dateFilter) return;

      // Create a filter using the date filter data
      newFilter = FilterProto.create({
        filter_id: uuidv4(),
        kg_column_header: selectedItem?.column?.kg_column_header,
        operator: FilterOperator.EQ,
        date_filter: dateFilter,
      });
    }

    if (newFilter) {
      // Add to filters
      setFilters([...filters, newFilter]);

      // Reset state
      setSelectedItem(null);
      setSelectedValues([]);
      setAttributeValues([]);
      setDateFilter(undefined);
      setEditingFilter(null);
      setEditingValues([]);
    }
  };

  // Function to remove a filter
  const handleRemoveFilter = (filterId: string) => {
    setFilters(filters.filter(filter => filter.filter_id !== filterId));
  };

  // Function to handle editing a filter
  const handleEditFilter = async (filter: FilterProto) => {
    // Remove the filter from the selected filters list first
    setFilters(filters.filter(f => f?.filter_id !== filter?.filter_id));
    // Set the filter being edited
    setEditingFilter(filter);
    // Find the corresponding dropdown item
    const correspondingItem = dropdownItems?.find(
      item => item?.column?.kg_column_header?.id === filter?.kg_column_header?.id
    );

    if (correspondingItem) {
      if (correspondingItem.type === "attribute") {
        // For attribute filters, store the values to be selected after fetch
        setEditingValues(filter.values || []);
      }
      setSelectedItem(correspondingItem);
    }
  };

  // Handler for selecting a new item from dropdown (for adding new filters)
  const handleSelectNewItem = (item: DropdownItem) => {
    setSelectedItem(item);
    // Reset values for new filter creation
    setSelectedValues([]);
    setAttributeValues([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
  };

  // Function to cancel current action (editing or adding)
  const handleCancel = () => {
    // If we were editing a filter, restore it to the filters list
    if (editingFilter) {
      setFilters([...filters, editingFilter]);
    }

    // Reset all state
    setEditingFilter(null);
    setSelectedItem(null);
    setSelectedValues([]);
    setAttributeValues([]);
    setDateFilter(undefined);
    setDateFilterCategory(DateFilterCategory.FIXED);
    setEditingValues([]);
  };

  // Helper function to get item display name
  const getItemDisplayName = (item: DropdownItem | null): string => {
    return item?.column?.kg_column_header?.name || "Unnamed";
  };

  return (
    <Stack spacing={3}>
      {/* Column Selection Dropdown */}
      <FormControl>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={dropdownItems.length === 0 || editingFilter !== null}
            fontWeight="600"
            color="fg.default"
          >
            {editingFilter
              ? `Editing: ${getItemDisplayName(selectedItem)}`
              : selectedItem
                ? getItemDisplayName(selectedItem)
                : dropdownItems.length === 0
                  ? "No columns available"
                  : filters.length === 0
                    ? "Add filter"
                    : "Add more filters"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {dropdownItems?.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleSelectNewItem(item)}
                  _hover={{ bg: "purple.50" }}
                  fontWeight="600"
                >
                  <Flex alignItems="center" width="100%">
                    {getItemDisplayName(item)}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Values Selection - only visible when an item is selected */}
      {selectedItem && (
        <FormControl>
          <FormLabel ml={1}>
            {selectedItem.type === "attribute"
              ? "Select values for " + getItemDisplayName(selectedItem)
              : "Define date for " + getItemDisplayName(selectedItem)}
          </FormLabel>

          {selectedItem.type === "attribute" ? (
            // For attribute columns - show multi-select
            isLoading ? (
              <Flex justify="center" align="center" py={4}>
                <Spinner size="sm" mr={2} />
                <Text mb={0}>Loading values...</Text>
              </Flex>
            ) : (
              <ColumnValues
                contextValues={attributeValues}
                selectedOptions={selectedValues}
                setSelectedOptions={setSelectedValues}
                errorMsg={errorMsg}
              />
            )
          ) : (
            // For temporal columns - show GlobalEntity date filter component
            <Box>
              <DateFilterPicker
                key={editingFilter ? "edit" : "new"}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                dateFilterCategory={dateFilterCategory}
                setDateFilterCategory={setDateFilterCategory}
              />
            </Box>
          )}
        </FormControl>
      )}

      {/* Add/Update Filter Button - only visible when an item is selected */}
      {selectedItem && (
        <Flex gap={2} width="100%" alignItems="center" justifyContent="center">
          <Button
            colorScheme="purple"
            onClick={handleAddFilter}
            isDisabled={
              (selectedItem.type === "attribute" && selectedValues.length === 0) ||
              (selectedItem.type === "temporal" && !dateFilter)
            }
            flex={1}
            bg="purple.700"
            leftIcon={<Icon as={TbFilterCheck} boxSize={5} />}
          >
            {editingFilter ? "Update Filter" : "Add Filter"}
          </Button>
          <Button variant="outline" colorScheme="gray" onClick={handleCancel} flex={1}>
            Cancel
          </Button>
        </Flex>
      )}

      {/* Display added filters */}
      {filters.length > 0 && (
        <Box mt={2}>
          <Stack spacing={2}>
            {filters?.map(filter => (
              <Flex
                key={filter.filter_id}
                borderWidth="1px"
                borderRadius="md"
                p={1}
                justifyContent="space-between"
                alignItems="center"
                backgroundColor="gray.50"
                borderColor="purple.700"
              >
                <Flex alignItems="center" gap={2} flexWrap="wrap">
                  <Text fontWeight="700" fontSize="sm" mb={0} ml={1}>
                    {filter.kg_column_header?.name || "Unnamed"}:
                  </Text>
                  <Badge colorScheme="purple" fontSize="sm" textTransform="none" bg="purple.700" color="white">
                    {getFilterDisplayString(filter)}
                  </Badge>
                </Flex>
                {/* Action icons: Edit and Remove */}
                <Flex alignItems="center" gap={1}>
                  <IconButton
                    aria-label="Edit filter"
                    icon={<Icon as={EditIcon} boxSize={4} color="purple.700" />}
                    onClick={() => handleEditFilter(filter)}
                    size="xs"
                    variant="ghost"
                    colorScheme="purple"
                    isDisabled={editingFilter !== null && editingFilter.filter_id !== filter.filter_id}
                  />
                  <IconButton
                    aria-label="Remove filter"
                    icon={<CloseIcon boxSize={3} />}
                    onClick={() => handleRemoveFilter(filter?.filter_id || "")}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                  />
                </Flex>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default Filters;
